import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Mic,
  Users,
  UserPlus,
  Activity,
  Stethoscope,
  MessageSquare,
  Settings,
  Search,
  FileText,
  Bot,
  MicOff,
  Volume2,
  VolumeX,
  Calendar,
} from "lucide-react";
import agentActionService from "../services/agentActions";

const FloatingAgent = () => {
  const [isPortalOpen, setIsPortalOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [isPortalFading, setIsPortalFading] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Voice integration state
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [agentState, setAgentState] = useState("idle"); // idle, listening, processing, speaking, error
  const [currentResponse, setCurrentResponse] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [showChatPanel, setShowChatPanel] = useState(false);

  const location = useLocation();
  const fadeTimeoutRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const openaiServiceRef = useRef(null);
  const responseTimeoutRef = useRef(null);

  // Get current route context for contextual buttons
  const currentPath = location.pathname;
  const isPatientList = currentPath === "/" || currentPath === "/patients";
  const isPatientDetail =
    currentPath.includes("/patients/") &&
    !currentPath.includes("/edit") &&
    !currentPath.includes("/new");
  const isPatientForm =
    currentPath.includes("/new") || currentPath.includes("/edit");
  const isDashboard = currentPath === "/dashboard";

  // Portal button configurations based on context
  const getContextualButtons = () => {
    const baseButtons = [
      // Voice commands now enabled with voice system integration
      {
        id: "voice",
        icon: voiceConnected ? (isListening ? MicOff : Mic) : Mic,
        label: voiceConnected
          ? isListening
            ? "Stop Listening"
            : "Start Voice"
          : "Connect Voice",
        color: voiceConnected
          ? isListening
            ? "bg-red-500"
            : "bg-green-500"
          : "bg-purple-500",
        action: "voice-activate",
        onClick: handleVoiceToggle,
        position: { angle: 0, distance: 90 }, // Top
      },
      {
        id: "chat",
        icon: MessageSquare,
        label: "Chat History",
        color: "bg-blue-500",
        action: "toggle-chat",
        onClick: () => setShowChatPanel(!showChatPanel),
        position: { angle: 90, distance: 90 }, // Right
      },
      {
        id: "settings",
        icon: Settings,
        label: "Settings",
        color: "bg-gray-500",
        action: "open-settings",
        onClick: () => agentActionService.execute("open-settings"),
        position: { angle: 180, distance: 90 }, // Bottom
      },
    ];

    if (isPatientList) {
      return [
        ...baseButtons,
        {
          id: "new-patient",
          icon: UserPlus,
          label: "New Patient",
          color: "bg-blue-500",
          action: "add-patient",
          onClick: () => agentActionService.execute("add-patient"),
          position: { angle: 45, distance: 90 }, // Top-right
        },
        {
          id: "search",
          icon: Search,
          label: "Quick Search",
          color: "bg-green-500",
          action: "search-patients",
          onClick: () => agentActionService.execute("search-patients"),
          position: { angle: 315, distance: 90 }, // Bottom-right
        },
        {
          id: "dashboard",
          icon: Activity,
          label: "Dashboard",
          color: "bg-orange-500",
          action: "view-dashboard",
          onClick: () => agentActionService.execute("view-dashboard"),
          position: { angle: 225, distance: 90 }, // Bottom-left
        },
        {
          id: "export",
          icon: FileText,
          label: "Export Data",
          color: "bg-indigo-500",
          action: "export-data",
          onClick: () => agentActionService.execute("export-data"),
          position: { angle: 135, distance: 90 }, // Top-left
        },
        {
          id: "new-appointment",
          icon: Calendar,
          label: "New Appointment",
          color: "bg-purple-500",
          action: "add-appointment",
          onClick: () => (window.location.href = "/appointments/new"),
          position: { angle: 270, distance: 90 }, // Left
        },
      ];
    }

    if (isPatientDetail) {
      return [
        ...baseButtons,
        {
          id: "new-visit",
          icon: FileText,
          label: "New Visit",
          color: "bg-green-500",
          action: "add-visit",
          onClick: () => agentActionService.execute("add-visit"),
          position: { angle: 45, distance: 90 },
        },
        {
          id: "ai-analyze",
          icon: Bot,
          label: "AI Analysis",
          color: "bg-purple-500",
          action: "generate-analysis",
          onClick: () => agentActionService.execute("generate-analysis"),
          position: { angle: 315, distance: 90 },
        },
        {
          id: "vitals",
          icon: Stethoscope,
          label: "Quick Vitals",
          color: "bg-red-500",
          action: "record-vitals",
          onClick: () => agentActionService.execute("record-vitals"),
          position: { angle: 225, distance: 90 },
        },
        {
          id: "back-to-list",
          icon: Users,
          label: "Patient List",
          color: "bg-blue-500",
          action: "view-patients",
          onClick: () => agentActionService.execute("view-patients"),
          position: { angle: 135, distance: 90 },
        },
        {
          id: "schedule-appointment",
          icon: Calendar,
          label: "Schedule Appointment",
          color: "bg-purple-500",
          action: "schedule-appointment",
          onClick: () => {
            const patientId = currentPath.split("/")[2];
            window.location.href = `/appointments/new?patientId=${patientId}`;
          },
          position: { angle: 270, distance: 90 }, // Left
        },
      ];
    }

    if (isPatientForm) {
      return [
        ...baseButtons,
        // Voice dictation disabled until voice system is implemented
        // {
        //   id: "voice-dictation",
        //   icon: MessageSquare,
        //   label: "Voice Dictation",
        //   color: "bg-indigo-500",
        //   action: "start-dictation",
        //   onClick: () => agentActionService.execute("start-dictation"),
        //   position: { angle: 45, distance: 90 },
        // },
        {
          id: "patient-list",
          icon: Users,
          label: "Patient List",
          color: "bg-blue-500",
          action: "view-patients",
          onClick: () => agentActionService.execute("view-patients"),
          position: { angle: 135, distance: 90 },
        },
      ];
    }

    if (isDashboard) {
      return [
        ...baseButtons,
        {
          id: "patients",
          icon: Users,
          label: "All Patients",
          color: "bg-blue-500",
          action: "view-patients",
          onClick: () => agentActionService.execute("view-patients"),
          position: { angle: 45, distance: 90 },
        },
        {
          id: "new-patient",
          icon: UserPlus,
          label: "New Patient",
          color: "bg-blue-500",
          action: "add-patient",
          onClick: () => agentActionService.execute("add-patient"),
          position: { angle: 135, distance: 90 },
        },
      ];
    }

    // Appointment pages
    if (currentPath.includes("/appointments")) {
      return [
        ...baseButtons,
        {
          id: "todays-schedule",
          icon: Calendar,
          label: "Today's Schedule",
          color: "bg-blue-500",
          action: "todays-schedule",
          onClick: () => agentActionService.showTodaysSchedule(),
          position: { angle: 45, distance: 90 },
        },
        {
          id: "calendar-view",
          icon: Calendar,
          label: "Calendar View",
          color: "bg-green-500",
          action: "calendar-view",
          onClick: () => (window.location.href = "/appointments/calendar"),
          position: { angle: 315, distance: 90 },
        },
        {
          id: "new-appointment",
          icon: UserPlus,
          label: "New Appointment",
          color: "bg-purple-500",
          action: "add-appointment",
          onClick: () => (window.location.href = "/appointments/new"),
          position: { angle: 225, distance: 90 },
        },
        {
          id: "patient-list",
          icon: Users,
          label: "Patient List",
          color: "bg-orange-500",
          action: "view-patients",
          onClick: () => (window.location.href = "/patients"),
          position: { angle: 135, distance: 90 },
        },
      ];
    }

    return baseButtons;
  };

  // Initialize voice service integration
  useEffect(() => {
    const initializeVoiceService = async () => {
      try {
        // Dynamically import the OpenAI Realtime service
        const { openaiRealtimeService } = await import(
          "../services/openaiRealtime.js"
        );
        openaiServiceRef.current = openaiRealtimeService;

        // Initialize the service with default configuration
        await openaiServiceRef.current.initialize({
          apiKey: import.meta.env.VITE_OPENAI_API_KEY,
          voice: "marin",
          temperature: 0.8,
          maxTokens: 4096,
        });

        console.log("FloatingAgent: Voice service initialized successfully");

        // Set up event listeners for voice service
        setupVoiceEventListeners();
      } catch (error) {
        console.error("Failed to initialize voice service:", error);
        setAgentState("error");
      }
    };

    initializeVoiceService();

    // Cleanup on unmount
    return () => {
      if (openaiServiceRef.current) {
        cleanupVoiceEventListeners();
      }
    };
  }, []);

  // Set up voice service event listeners
  const setupVoiceEventListeners = () => {
    const service = openaiServiceRef.current;
    if (!service) return;

    // Connection events
    service.on("connected", () => {
      console.log("FloatingAgent: Voice service connected successfully!");
      setVoiceConnected(true);
      setAgentState("idle");
    });

    service.on("sessionCreated", (session) => {
      console.log("FloatingAgent: OpenAI session created:", session);
    });

    service.on("disconnected", () => {
      console.log("FloatingAgent: Voice service disconnected");
      setVoiceConnected(false);
      setAgentState("idle");
      setIsListening(false);
      setIsAgentActive(false);
    });

    // Voice activity events
    service.on("voiceActivity", (data) => {
      setVoiceLevel(data.level || 0);
      if (data.type === "speechStart") {
        setAgentState("listening");
        setIsListening(true);
      } else if (data.type === "speechEnd") {
        setAgentState("processing");
        setIsListening(false);
      }
    });

    // Text response events
    service.on("textComplete", (data) => {
      if (data.text) {
        setCurrentResponse(data.text);
        setShowResponse(true);

        // Add to chat history
        setChatHistory((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: "ai_response",
            content: data.text,
            timestamp: new Date(),
          },
        ]);

        // Auto-hide response after 5 seconds
        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current);
        }
        responseTimeoutRef.current = setTimeout(() => {
          setShowResponse(false);
          setCurrentResponse("");
        }, 5000);
      }
    });

    // Audio playback events
    service.on("audioPlaybackStart", () => {
      setAgentState("speaking");
    });

    service.on("audioPlaybackEnd", () => {
      setAgentState("idle");
    });

    // Command execution events
    service.on("commandExecuted", (data) => {
      setChatHistory((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "command_executed",
          content: `Executed: ${data.command}`,
          timestamp: new Date(),
        },
      ]);
    });

    // Error events
    service.on("error", (error) => {
      console.error("FloatingAgent: Voice service error:", error);
      setAgentState("error");
      setIsListening(false);
      setVoiceConnected(false);
      setIsAgentActive(false);
    });
  };

  // Clean up voice event listeners
  const cleanupVoiceEventListeners = () => {
    const service = openaiServiceRef.current;
    if (!service) return;

    service.off("connected");
    service.off("disconnected");
    service.off("sessionCreated");
    service.off("voiceActivity");
    service.off("textComplete");
    service.off("audioPlaybackStart");
    service.off("audioPlaybackEnd");
    service.off("commandExecuted");
    service.off("error");
  };

  // Handle voice toggle
  const handleVoiceToggle = async () => {
    const service = openaiServiceRef.current;
    if (!service) {
      console.error("Voice service not initialized");
      setAgentState("error");
      return;
    }

    try {
      if (!voiceConnected) {
        // Ensure service is initialized before connecting
        if (!service.config) {
          console.log("Initializing voice service configuration...");
          await service.initialize({
            apiKey: import.meta.env.VITE_OPENAI_API_KEY,
            voice: "marin",
            temperature: 0.8,
            maxTokens: 4096,
          });
          console.log("Voice service configuration initialized");
        }

        // Connect to voice service
        console.log("Connecting to voice service...");
        await service.connect("doctor-1"); // TODO: Use actual doctor ID
        setIsAgentActive(true);
      } else if (isListening) {
        // Stop recording
        console.log("Stopping voice recording...");
        await service.stopRecording();
        setIsListening(false);
        setAgentState("idle");
      } else {
        // Start recording
        console.log("Starting voice recording...");
        await service.startRecording();
        setIsListening(true);
        setAgentState("listening");
      }
    } catch (error) {
      console.error("Voice toggle error:", error);
      setAgentState("error");

      // Reset states on error
      setVoiceConnected(false);
      setIsListening(false);
      setIsAgentActive(false);
    }
  };

  const handleAgentClick = () => {
    if (isPortalOpen) {
      closePortal();
    } else {
      openPortal();
    }
  };

  const openPortal = () => {
    // Clear any existing timeouts
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    setIsPortalFading(false);
    setIsPortalOpen(true);
  };

  const closePortal = () => {
    setIsPortalFading(true);

    // Start fade out after a small delay
    fadeTimeoutRef.current = setTimeout(() => {
      setIsPortalOpen(false);
      setIsPortalFading(false);
    }, 500); // 500ms fade duration
  };

  const handlePortalEnter = () => {
    openPortal();
  };

  const handlePortalLeave = () => {
    // Delay before starting close sequence
    hoverTimeoutRef.current = setTimeout(() => {
      closePortal();
    }, 200); // Small delay before starting fade
  };

  // Load agent visibility preference and listen for changes
  useEffect(() => {
    // Load initial visibility setting
    const savedVisibility = localStorage.getItem("agentVisible");
    if (savedVisibility !== null) {
      setIsVisible(savedVisibility === "true");
    }

    // Listen for visibility changes from dashboard
    const handleVisibilityChange = (event) => {
      setIsVisible(event.detail.isVisible);
    };

    window.addEventListener("agentVisibilityChanged", handleVisibilityChange);

    return () => {
      window.removeEventListener(
        "agentVisibilityChanged",
        handleVisibilityChange
      );
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
    };
  }, []);

  // Convert polar coordinates to cartesian for button positioning
  const getButtonPosition = (angle, distance) => {
    const radian = (angle * Math.PI) / 180;
    return {
      x: Math.cos(radian) * distance,
      y: Math.sin(radian) * distance,
    };
  };

  const contextualButtons = getContextualButtons();

  // Don't render if agent is hidden
  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`
        fixed z-50 w-64 h-64 flex items-center justify-center transition-all duration-300
        ${isPortalOpen ? "bg-black bg-opacity-5 rounded-full" : ""}
      `}
      style={{ bottom: "24px", left: "24px" }}
      onMouseEnter={handlePortalEnter}
      onMouseLeave={handlePortalLeave}
    >
      {/* Portal Buttons */}
      {isPortalOpen && (
        <div
          className={`
            absolute inset-0 pointer-events-none transition-opacity duration-500
            ${isPortalFading ? "opacity-0" : "opacity-100"}
          `}
        >
          {contextualButtons.map((button, index) => {
            const pos = getButtonPosition(
              button.position.angle,
              button.position.distance
            );
            const delay = index * 50; // Stagger animation

            return (
              <div
                key={button.id}
                className="absolute pointer-events-auto group"
                style={{
                  left: `calc(50% + ${pos.x}px - 24px)`,
                  top: `calc(50% - ${pos.y}px - 24px)`,
                  animationDelay: `${delay}ms`,
                }}
              >
                {/* Radial Background */}
                <div
                  className={`
                    absolute inset-0 w-12 h-12 rounded-full transition-all duration-200
                    ${button.color} opacity-40 group-hover:opacity-100
                    transform group-hover:scale-110
                  `}
                />

                {/* Icon Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    button.onClick();
                    closePortal();
                  }}
                  className={`
                    relative w-12 h-12 rounded-full transition-all duration-200
                    transform group-hover:scale-105 agent-portal-button
                    text-white z-10
                  `}
                  title={button.label}
                >
                  <button.icon size={20} className="mx-auto" />

                  {/* Tooltip */}
                  <span
                    className="
                    absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
                    px-2 py-1 text-xs bg-gray-900 text-white rounded
                    opacity-0 group-hover:opacity-100 transition-opacity duration-200
                    whitespace-nowrap pointer-events-none z-20
                    dark:bg-gray-100 dark:text-gray-900
                  "
                  >
                    {button.label}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Agent Button */}
      <div className="relative">
        {/* Voice Level Ring Indicator */}
        <div
          className={`
            absolute inset-0 rounded-full transition-all duration-300
            ${
              agentState === "listening"
                ? "ring-4 ring-green-400 ring-opacity-80 animate-pulse"
                : agentState === "processing"
                ? "ring-4 ring-yellow-400 ring-opacity-60 animate-spin"
                : agentState === "speaking"
                ? "ring-4 ring-blue-400 ring-opacity-80 animate-pulse"
                : agentState === "error"
                ? "ring-4 ring-red-400 ring-opacity-80"
                : voiceConnected
                ? "ring-2 ring-purple-400 ring-opacity-40"
                : "ring-2 ring-medical-blue ring-opacity-30"
            }
          `}
        />

        {/* Voice Level Visualization */}
        {voiceLevel > 0 && agentState === "listening" && (
          <div
            className="absolute inset-0 rounded-full bg-green-400 opacity-20 voice-level-indicator"
            style={{
              transform: `scale(${1 + voiceLevel * 0.5})`,
              transition: "transform 0.1s ease-out",
            }}
          />
        )}

        {/* Agent Button */}
        <button
          onClick={handleAgentClick}
          className={`
            relative w-16 h-16 rounded-full shadow-xl transition-all duration-300
            transform hover:scale-110 active:scale-95
            ${
              agentState === "listening"
                ? "bg-gradient-to-r from-green-500 to-green-600 agent-voice-listening"
                : agentState === "processing"
                ? "bg-gradient-to-r from-yellow-500 to-orange-500 agent-voice-processing"
                : agentState === "speaking"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 agent-voice-speaking"
                : agentState === "error"
                ? "bg-gradient-to-r from-red-500 to-red-600"
                : voiceConnected
                ? "bg-gradient-to-r from-purple-500 to-purple-600"
                : "bg-gradient-to-r from-stone-100 to-stone-300"
            }
            text-white hover:shadow-2xl
            dark:shadow-black/30
          `}
        >
          {/* Agent Icon */}
          <div className="flex items-center justify-center w-full h-full">
            {agentState === "listening" ? (
              <Mic size={24} className="animate-pulse" />
            ) : agentState === "processing" ? (
              <Bot size={24} className="animate-spin" />
            ) : agentState === "speaking" ? (
              <Volume2 size={24} className="animate-pulse" />
            ) : agentState === "error" ? (
              <VolumeX size={24} />
            ) : (
              <img
                src="/agent_level_3.svg"
                alt="Medical Agent"
                className={`w-10 h-10 object-contain ${
                  voiceConnected ? "animate-bounce" : ""
                }`}
              />
            )}
          </div>

          {/* Status Indicator */}
          <div
            className={`
              absolute -top-1 -right-1 w-4 h-4 rounded-full transition-all duration-300
              ${
                agentState === "listening"
                  ? "bg-green-400 animate-pulse"
                  : agentState === "processing"
                  ? "bg-yellow-400 animate-pulse"
                  : agentState === "speaking"
                  ? "bg-blue-400 animate-pulse"
                  : agentState === "error"
                  ? "bg-red-400"
                  : voiceConnected
                  ? "bg-purple-400"
                  : "bg-gray-400"
              }
            `}
          />
        </button>

        {/* Portal Open Indicator */}
        {isPortalOpen && !isPortalFading && (
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-medical-blue opacity-50 agent-portal-ring" />
        )}
      </div>

      {/* Floating Response Text */}
      {showResponse && currentResponse && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 max-w-xs voice-response-text">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-600">
            <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
              {currentResponse}
            </div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white dark:border-t-gray-800"></div>
            </div>
          </div>
        </div>
      )}

      {/* Chat History Panel */}
      {showChatPanel && (
        <div className="absolute bottom-20 left-20 w-80 max-h-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden chat-panel">
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                Chat History
              </h3>
              <button
                onClick={() => setShowChatPanel(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ×
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-80 p-2">
            {chatHistory.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                No conversation history yet.
                <br />
                Start by using voice commands!
              </div>
            ) : (
              chatHistory.map((message) => (
                <div
                  key={message.id}
                  className="mb-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium ${
                        message.type === "ai_response"
                          ? "text-blue-600 dark:text-blue-400"
                          : message.type === "command_executed"
                          ? "text-green-600 dark:text-green-400"
                          : "text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {message.type === "ai_response"
                        ? "AI Response"
                        : message.type === "command_executed"
                        ? "Command"
                        : "System"}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-800 dark:text-gray-200">
                    {message.content}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingAgent;
